import { useEffect, useRef } from 'react'
import Styles from './ChatMessages.module.scss'
import { collectResults } from 'utils/query'
import { useInfiniteMessages } from 'hooks/queries/chat/useInfiniteMessages'
import { sortBy } from 'lodash'
import { Message } from './Message/Message'
import { LoadingState } from './LoadingState'
import { ArrowUp } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { ChatMessagesProps } from './Types'
import { Suggestions } from './Suggestions'
import { AIJobStatus } from 'types'

const ChatMessages = ({ threadId, setValue }: ChatMessagesProps) => {
  const messagesQuery = useInfiniteMessages({ threadId, items: 5 })
  const messages = collectResults(messagesQuery, (data) => data.messages)
  const sortedMessages = sortBy(messages, (message) => message.created_at)
  const { hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isFetching } = messagesQuery
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const showLoading = isFetchingNextPage || isFetching || isLoading
  const lastMessageContent = sortedMessages.length > 0 ? sortedMessages[sortedMessages.length - 1].content : null
  const lastMessageContentRef = useRef(lastMessageContent)
  const bottomRef = useRef<HTMLDivElement>(null)
  const initialLoad = useRef(true)
  const isInProgress = sortedMessages.some(
    (message) => message.status && [AIJobStatus.Pending, AIJobStatus.Started].includes(message.status),
  )

  useEffect(() => {
    if (lastMessageContent !== lastMessageContentRef.current) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    }
    lastMessageContentRef.current = lastMessageContent
  }, [lastMessageContent])

  useEffect(() => {
    if (initialLoad.current && sortedMessages.length > 0) {
      initialLoad.current = false
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [sortedMessages])

  return (
    <div className={Styles.messagesContainer}>
      <div className={Styles.loadingContainer}>
        {hasNextPage && (
          <Button
            frontIcon={ArrowUp}
            size="xs"
            variant="editor"
            isDisabled={showLoading}
            onPress={() => fetchNextPage()}
          >
            Load more
          </Button>
        )}
        {showLoading && <LoadingState count={2} />}
      </div>
      <div className={Styles.messages} ref={messagesContainerRef}>
        {sortedMessages.map((message, index) => (
          <Message key={index} message={message} isLastMessage={index === sortedMessages.length - 1} />
        ))}
        {!isInProgress && sortedMessages.length === 0 && <Suggestions setValue={setValue} />}
      </div>
      <div ref={bottomRef} />
    </div>
  )
}

export { ChatMessages }
